import { useEffect, useState } from 'react';
import { dashboardAPI } from '../../services/api';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from 'recharts';

const fmt = (v) => `R$ ${parseFloat(v||0).toFixed(2).replace('.',',')}`;
const fmtShort = (v) => {
  const n = parseFloat(v||0);
  return n >= 1000 ? `R$ ${(n/1000).toFixed(1)}k` : `R$ ${n.toFixed(0)}`;
};

const STATUS_MAP = {
  waiting:         { label: 'Aguardando',    color: '#F59E0B' },
  in_progress:     { label: 'Em andamento',  color: '#2563EB' },
  waiting_payment: { label: 'Ag. pagamento', color: '#D97706' },
  done:            { label: 'Finalizado',    color: '#10B981' },
  delivered:       { label: 'Entregue',      color: '#059669' },
  cancelled:       { label: 'Cancelado',     color: '#EF4444' },
};

const StatusBadge = ({ status }) => {
  const s = STATUS_MAP[status] || { label: status, color: '#6B7280' };
  const bg = s.color + '22';
  return (
    <span style={{ background: bg, color: s.color, borderRadius: 6, padding: '2px 8px', fontSize: '.75rem', fontWeight: 600 }}>
      {s.label}
    </span>
  );
};

export default function Dashboard() {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    dashboardAPI.index()
      .then(r => setData(r.data.data))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300, color: '#9CA3AF' }}>
      Carregando...
    </div>
  );
  if (!data) return null;

  const cards = [
    { label: 'Agendamentos hoje', value: data.today_appointments, icon: '📅', color: '#2563EB', bg: '#DBEAFE' },
    { label: 'O.S. em andamento', value: data.active_orders,      icon: '📋', color: '#D97706', bg: '#FEF3C7' },
    { label: 'Faturamento do mês', value: fmt(data.month_revenue), icon: '💰', color: '#059669', bg: '#D1FAE5' },
    { label: 'Ticket médio',       value: fmt(data.avg_ticket),    icon: '🎯', color: '#7C3AED', bg: '#EDE9FE' },
    { label: 'Clientes novos',     value: data.new_customers,      icon: '👥', color: '#0891B2', bg: '#CFFAFE' },
    { label: 'A receber',          value: fmt(data.pending_income), icon: '📥', color: '#DC2626', bg: '#FEE2E2' },
  ];

  // Donut data: orders by status (last 30 days)
  const statusData = Object.entries(data.orders_by_status || {})
    .map(([status, qty]) => ({ name: STATUS_MAP[status]?.label || status, value: Number(qty), color: STATUS_MAP[status]?.color || '#6B7280' }))
    .filter(d => d.value > 0);

  // Revenue trend (last 14 days) — fill gaps so the chart is continuous
  const revenueByDay = (data.revenue_by_day || []).map(r => ({
    date:    new Date(r.date + 'T00:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }),
    revenue: parseFloat(r.revenue),
    orders:  parseInt(r.orders),
  }));

  return (
    <div>
      <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#0F172A', marginBottom: 24 }}>Dashboard</h1>

      {/* KPI Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(200px,1fr))', gap: 16, marginBottom: 28 }}>
        {cards.map(c => (
          <div key={c.label} style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 12, padding: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <span style={{ fontSize: '.8rem', color: '#6B7280' }}>{c.label}</span>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: c.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem' }}>{c.icon}</div>
            </div>
            <div style={{ fontSize: '1.4rem', fontWeight: 700, color: c.color }}>{c.value}</div>
          </div>
        ))}
      </div>

      {/* Revenue trend + Orders by status */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 20, marginBottom: 20 }}>
        {/* Revenue trend */}
        <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 12, padding: 20 }}>
          <h3 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: 16, color: '#0F172A' }}>Receita — últimos 14 dias</h3>
          {revenueByDay.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={revenueByDay}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={fmtShort} width={60} />
                <Tooltip formatter={(v, n) => n === 'revenue' ? fmt(v) : `${v} O.S.`} labelFormatter={l => `Dia ${l}`} />
                <Line type="monotone" dataKey="revenue" stroke="#2563EB" strokeWidth={2.5} dot={{ r: 3 }} name="Receita" />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9CA3AF', fontSize: '.9rem' }}>
              Nenhum faturamento nos últimos 14 dias.
            </div>
          )}
        </div>

        {/* Orders by status donut */}
        <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 12, padding: 20 }}>
          <h3 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: 16, color: '#0F172A' }}>O.S. por status</h3>
          {statusData.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={140}>
                <PieChart>
                  <Pie data={statusData} dataKey="value" cx="50%" cy="50%" innerRadius={40} outerRadius={65}>
                    {statusData.map((d, i) => <Cell key={i} fill={d.color} />)}
                  </Pie>
                  <Tooltip formatter={(v, n) => [`${v} O.S.`, n]} />
                </PieChart>
              </ResponsiveContainer>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginTop: 4 }}>
                {statusData.map(d => (
                  <div key={d.name} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '.75rem' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                      <span style={{ width: 8, height: 8, borderRadius: '50%', background: d.color, display: 'inline-block' }} />
                      {d.name}
                    </span>
                    <span style={{ fontWeight: 700, color: '#374151' }}>{d.value}</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 160, color: '#9CA3AF', fontSize: '.9rem' }}>
              Nenhuma O.S. ainda.
            </div>
          )}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
        {/* Top Services */}
        <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 12, padding: 20 }}>
          <h3 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: 16, color: '#0F172A' }}>Serviços mais vendidos (mês)</h3>
          {data.top_services?.length ? (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={data.top_services} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                <XAxis type="number" tick={{ fontSize: 11 }} />
                <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} width={120} />
                <Tooltip formatter={(v, n) => n === 'qty' ? `${v} vendas` : fmt(v)} />
                <Bar dataKey="qty" fill="#2563EB" radius={[0, 4, 4, 0]} name="Qtd" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9CA3AF', fontSize: '.9rem' }}>
              Nenhum dado ainda.
            </div>
          )}
        </div>

        {/* Próximos agendamentos */}
        <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 12, padding: 20 }}>
          <h3 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: 16, color: '#0F172A' }}>Próximos agendamentos</h3>
          {data.upcoming_appointments?.length ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {data.upcoming_appointments.map((a, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px', background: '#F8FAFC', borderRadius: 8 }}>
                  <div style={{ background: '#2563EB', color: '#fff', borderRadius: 6, padding: '4px 8px', fontSize: '.75rem', fontWeight: 600, flexShrink: 0 }}>
                    {new Date(a.scheduled_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: '.85rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.customer}</div>
                    <div style={{ fontSize: '.75rem', color: '#6B7280' }}>{a.plate} · {a.service_name}</div>
                  </div>
                  <StatusBadge status={a.status} />
                </div>
              ))}
            </div>
          ) : (
            <p style={{ color: '#9CA3AF', fontSize: '.9rem' }}>Nenhum agendamento futuro.</p>
          )}
        </div>
      </div>

      {/* Estoque baixo */}
      {data.low_stock?.length > 0 && (
        <div style={{ background: '#FEF3C7', border: '1px solid #FDE68A', borderRadius: 12, padding: 20 }}>
          <h3 style={{ fontSize: '1rem', fontWeight: 700, color: '#92400E', marginBottom: 12 }}>Estoque Baixo</h3>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            {data.low_stock.map((p, i) => (
              <div key={i} style={{ background: '#fff', border: '1px solid #FDE68A', borderRadius: 8, padding: '8px 14px' }}>
                <div style={{ fontWeight: 600, fontSize: '.85rem' }}>{p.name}</div>
                <div style={{ fontSize: '.75rem', color: '#92400E' }}>Qtd: {p.quantity} (mín: {p.min_quantity})</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
